import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { WikiBacklink, WikiDiagnostic } from "@/api";
import { MARKDOWN_LINK_CLASS } from "@/components/chat/MarkdownMedia";
import { Text } from "@/components/Text";
import { fileLabel } from "./WikiFileList";
import { wikiFileHref } from "./wikilinks";

/** Indexer findings for this page (editors only: the API sends none to viewers). */
export function WikiIssues({ diagnostics }: Readonly<{ diagnostics: WikiDiagnostic[] }>) {
  const { t } = useTranslation("knowledge");
  if (diagnostics.length === 0) return null;
  return (
    <section aria-label={t("document.issues")} className="flex flex-col gap-2 rounded-lg bg-surface-secondary p-4">
      <Text variant="eyebrow" tone="tertiary" as="h3">
        {t("document.issues")}
      </Text>
      <ul className="flex flex-col gap-1.5">
        {diagnostics.map((d) => (
          <li key={`${d.code}:${d.message}`} className="flex gap-2">
            <Text variant="caption" tone={d.severity === "error" ? "error" : "warning"} className="shrink-0">
              {d.severity === "error" ? t("document.error") : t("document.warning")}
            </Text>
            <Text variant="caption" tone="secondary" className="min-w-0 break-words">
              {d.message}
            </Text>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Pages that link here and that the reader may see. */
export function WikiBacklinks({ backlinks }: Readonly<{ backlinks: WikiBacklink[] }>) {
  const { t } = useTranslation("knowledge");
  if (backlinks.length === 0) return null;
  return (
    <section aria-label={t("document.backlinks")} className="flex flex-col gap-2 border-t border-border-subtle pt-4">
      <Text variant="eyebrow" tone="tertiary" as="h3">
        {t("document.backlinks")}
      </Text>
      <ul className="flex flex-col gap-1">
        {backlinks.map((b) => (
          <li key={`${b.spaceUuid}:${b.path}`}>
            <Link to={wikiFileHref(b.spaceUuid, b.path)} className={`btn-focus-ring rounded ${MARKDOWN_LINK_CLASS}`}>
              {fileLabel(b)}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
